import { Component, OnInit, Input, OnChanges } from '@angular/core';
import { Router } from '@angular/router';
import { NgbDateParserFormatter, NgbCalendar } from '@ng-bootstrap/ng-bootstrap';
import { NgbDateFRParserFormatter } from './../inspections-scheduled/ngb-date-fr-parser-formatter';
import { CommonService } from '../../services/common/common.service';
import { PayrollService } from '../../services/payroll/payroll.service';
import { downloadCsv, pageNumbers, REPORT_DATE_RANGES } from '../../report-view';
import { PayrollGroup, payrollGroupMatches, payrollGroups, payrollLineCount } from '../../payroll-view';

/**
 * Payroll Details (Charges) (mockup reports/payroll-details-charges). Same API (payroll_report_new),
 * filters, date presets and inspector rule as before; one collapsible card per inspector with the
 * API's own totals. Also embedded on the old admin dashboard / home widgets ([count]), where the
 * page head is hidden as before.
 */
@Component({
  selector: 'app-accounting-report-payroll',
  templateUrl: './accounting-report-payroll.component.html',
  providers: [{ provide: NgbDateParserFormatter, useClass: NgbDateFRParserFormatter }, CommonService, PayrollService],
  styleUrls: ['./accounting-report-payroll.component.scss']
})
export class AccountingReportPayrollComponent implements OnInit, OnChanges {
  @Input() count: number;
  readonly pageSizes = [5, 10, 25];
  readonly embedded: boolean;
  readonly dateRanges = REPORT_DATE_RANGES;

  report = this.emptyFilters();
  branches = [];
  inspectors = [];
  /** Branch and Inspector filters: not for inspectors (the API limits them to their own pay). */
  inspectorCheck = false;
  groups: PayrollGroup[] = [];
  loaded = false;
  errordetails = false;
  loading = false;
  search = '';
  pageSize = 5;
  page = 1;
  collapsed: { [key: string]: boolean } = {};
  today = new Date();

  constructor(private _router: Router, private calendar: NgbCalendar, private commonService: CommonService,
    private payrollService: PayrollService) {
    this.embedded = _router.url.split('?')[0] !== '/accounting-report-payroll-charge';
  }

  ngOnInit() {
    this.commonService.getBranch().subscribe(data => this.branches = data);
    const user = JSON.parse(localStorage.getItem('user') || 'null');
    this.inspectorCheck = !!user && user.user_level != 'inspector';
    if (this.inspectorCheck) {
      this.commonService.getInspectors().subscribe(data => this.inspectors = data);
    }
  }

  ngOnChanges() {
    // Date range picked on the dashboard widget.
    if (this.count) {
      this.report.dateRange = this.count;
      this.dateRangeSubmit(this.count);
      this.payRollReport();
    }
  }

  private emptyFilters() {
    return { branch: '', inspector: '', start: null, end: null, dateRange: 0 as any };
  }

  payRollReport() {
    if (!this.report.start || !this.report.end) {
      this.errordetails = true;
      return false;
    }
    this.errordetails = false;
    this.loading = true;
    this.payrollService.getPayRollNew(this.report).subscribe(
      data => {
        this.loading = false;
        this.loaded = true;
        this.today = new Date();
        this.groups = payrollGroups(data);
        this.collapsed = {};
        this.page = 1;
      },
      () => this.loading = false
    );
  }

  clearPayRoll() {
    this.report = this.emptyFilters();
    this.search = '';
    this.errordetails = false;
  }

  dateRangeSubmit(value) {
    this.report.end = this.calendar.getToday();
    this.report.start = this.commonService.dateRangeSubmit(value);
  }

  // ---- Cards: search, paging, collapse ----

  get filteredGroups(): PayrollGroup[] {
    return this.groups.filter(g => payrollGroupMatches(g, this.search));
  }

  get pageGroups(): PayrollGroup[] {
    const start = (this.page - 1) * this.pageSize;
    return this.filteredGroups.slice(start, start + this.pageSize);
  }

  get pages(): number {
    return Math.max(1, Math.ceil(this.filteredGroups.length / this.pageSize));
  }

  get pageNumbers(): number[] {
    return pageNumbers(this.page, this.pages);
  }

  goTo(page: number) {
    if (page >= 1 && page <= this.pages) {
      this.page = page;
    }
  }

  setPageSize(size: number) {
    this.pageSize = size;
    this.page = 1;
  }

  onSearch() {
    this.page = 1;
  }

  toggle(group: PayrollGroup) {
    this.collapsed[group.key] = !this.collapsed[group.key];
  }

  get lineCount(): number {
    return payrollLineCount(this.filteredGroups);
  }

  plural(n: number, one: string, many: string): string {
    return n + ' ' + (n === 1 ? one : many);
  }

  // Every charge of the matching inspectors, one line each.
  exportCsv() {
    const rows: any[][] = [['Inspector', 'Section', 'Date', 'Report #', 'Description', 'Property', 'Charges', 'Crew Amt.', 'Treat Amt.', 'Insp. Pay']];
    this.filteredGroups.forEach(g => {
      g.completions.forEach(l => rows.push([g.name, 'Completions', l.date, l.jobId, l.raw.description, l.raw.address,
        l.raw.charges_total, l.raw.crew_total, l.raw.treats_total, l.raw.inspectors_pay]));
      g.inspections.forEach(l => rows.push([g.name, 'Inspections', l.date, l.jobId, l.raw.description, l.raw.address,
        l.raw.charges_total, '', '', l.raw.inspectors_pay]));
    });
    downloadCsv('payroll-details-charges.csv', rows);
  }
}
